import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useArenaProfile } from 'shared/contexts/ArenaProfileContext';
import { isReportArea } from 'modules/arena/reports/model/areas';
import { todayIn } from 'modules/arena/reports/model/dates';
import {
  carryToArea,
  defaultFilters,
  removeChip as removeChipFrom,
  sanitizeFilters,
  validateRange,
} from 'modules/arena/reports/model/filters';
import {
  AreaResponse,
  MoneyBasis,
  ReportArea,
  ReportFilters,
  SpecificFilters,
  TableQuery,
} from 'modules/arena/reports/model/types';
import { createReportsSource } from 'modules/arena/reports/source';
import { DemoScenario } from 'modules/arena/reports/source/demoSource';
import { ReportContext } from 'modules/arena/reports/source/types';

const AREA_PARAM = 'tipo';
const FALLBACK_TIMEZONE = 'America/Fortaleza';

type ContextState =
  | { status: 'loading' }
  | { status: 'ok'; context: ReportContext }
  | { status: 'not_connected' | 'denied' | 'error'; message: string };

export type QueryState =
  | { status: 'idle' }
  | { status: 'loading'; previous: AreaResponse | null }
  | { status: 'done'; response: AreaResponse }
  | { status: 'error'; message: string };

export function useReportsController() {
  const { profile, isCustom } = useArenaProfile();
  const [scenario, setScenario] = useState<DemoScenario>('normal');
  const source = useMemo(() => createReportsSource(profile, isCustom, scenario), [profile, isCustom, scenario]);

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedArea = searchParams.get(AREA_PARAM);
  const area: ReportArea = isReportArea(requestedArea) ? requestedArea : 'visao-geral';

  const [contextState, setContextState] = useState<ContextState>({ status: 'loading' });
  const timezone = contextState.status === 'ok' ? contextState.context.catalog.timezone : FALLBACK_TIMEZONE;
  const today = todayIn(timezone);

  const [applied, setApplied] = useState<ReportFilters>(() => defaultFilters(area, today));
  const [draft, setDraft] = useState<ReportFilters>(applied);
  const [notices, setNotices] = useState<string[]>([]);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [moneyBasis, setMoneyBasis] = useState<MoneyBasis>('bruto');
  const [query, setQuery] = useState<QueryState>({ status: 'idle' });
  const [tableQuery, setTableQuery] = useState<TableQuery>({ page: 1 });
  const [tableLoading, setTableLoading] = useState(false);

  // Guarda contra respostas fora de ordem: só a consulta mais recente pode escrever na tela.
  const requestSeq = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const tableSeq = useRef(0);

  // ---- troca de arena/cenário: zera tudo do contexto anterior imediatamente ----
  useEffect(() => {
    controllerRef.current?.abort();
    requestSeq.current += 1;
    tableSeq.current += 1;
    setQuery({ status: 'idle' });
    setNotices([]);
    setRangeError(null);
    const fresh = defaultFilters(area, todayIn(FALLBACK_TIMEZONE));
    setApplied(fresh);
    setDraft(fresh);
    setTableQuery({ page: 1 });
    setContextState({ status: 'loading' });

    const controller = new AbortController();
    source
      .loadContext(controller.signal)
      .then((result) => {
        if (result.status === 'ok') setContextState({ status: 'ok', context: result.context });
        else setContextState({ status: result.status, message: result.message });
      })
      .catch((error: Error) => {
        if (error.name !== 'AbortError') setContextState({ status: 'error', message: error.message });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const context = contextState.status === 'ok' ? contextState.context : null;

  // Área sem permissão: não consulta dados e cai na primeira área permitida.
  const allowedAreas = context?.permissions.areas;
  useEffect(() => {
    if (allowedAreas && !allowedAreas.includes(area)) {
      setSearchParams(allowedAreas[0] === 'visao-geral' ? {} : { [AREA_PARAM]: allowedAreas[0] }, { replace: true });
    }
  }, [allowedAreas, area, setSearchParams]);

  const fetchArea = useCallback(
    (filters: ReportFilters, targetArea: ReportArea, table: TableQuery) => {
      if (!context || !context.permissions.areas.includes(targetArea)) return;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const id = ++requestSeq.current;
      setQuery((current) => ({ status: 'loading', previous: current.status === 'done' ? current.response : null }));

      source
        .fetchArea(targetArea, filters, table, controller.signal)
        .then((response) => {
          if (id !== requestSeq.current) return; // resposta antiga: descarta
          setQuery({ status: 'done', response });
        })
        .catch((error: Error) => {
          if (error.name === 'AbortError' || id !== requestSeq.current) return;
          setQuery({ status: 'error', message: error.message });
        });
    },
    [context, source]
  );

  // Carrega quando o contexto chega, quando a área muda ou quando filtros são aplicados.
  useEffect(() => {
    if (context) fetchArea(applied, area, tableQuery.sort ? { page: 1, sort: tableQuery.sort } : { page: 1 });
    // A tabela pagina por conta própria (fetchTable); aqui só a consulta da área.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, area, applied, fetchArea]);

  // Acesso negado: remove dados já exibidos.
  useEffect(() => {
    if (query.status === 'done' && query.response.status === 'denied') setTableQuery({ page: 1 });
  }, [query]);

  // ---------- ações ----------

  const selectArea = useCallback(
    (next: ReportArea, overrides?: SpecificFilters) => {
      const carried = carryToArea(next, applied, overrides);
      setApplied(carried);
      setDraft(carried);
      setNotices([]);
      setRangeError(null);
      setTableQuery({ page: 1 });
      setSearchParams(next === 'visao-geral' ? {} : { [AREA_PARAM]: next });
    },
    [applied, setSearchParams]
  );

  const updateDraft = useCallback(
    (next: ReportFilters) => {
      if (!context) return setDraft(next);
      const { filters, notices: changed } = sanitizeFilters(area, next, context.catalog);
      setDraft(filters);
      if (changed.length) setNotices(changed);
    },
    [area, context]
  );

  const applyDraft = useCallback(() => {
    if (!context) return;
    const error = validateRange(draft.common, source.capabilities, today);
    setRangeError(error);
    if (error) return;
    const { filters, notices: changed } = sanitizeFilters(area, draft, context.catalog);
    setNotices(changed);
    setTableQuery({ page: 1 });
    setApplied(filters);
    setDraft(filters);
  }, [area, context, draft, source.capabilities, today]);

  const discardDraft = useCallback(() => {
    setDraft(applied);
    setRangeError(null);
  }, [applied]);

  const removeChip = useCallback(
    (chipId: string) => {
      const next = removeChipFrom(chipId, area, applied, today);
      setApplied(next);
      setDraft(next);
      setTableQuery({ page: 1 });
    },
    [applied, area, today]
  );

  const clearFilters = useCallback(() => {
    const fresh = defaultFilters(area, today);
    setApplied(fresh);
    setDraft(fresh);
    setNotices([]);
    setRangeError(null);
    setTableQuery({ page: 1 });
  }, [area, today]);

  const retry = useCallback(() => {
    if (contextState.status === 'error') {
      setContextState({ status: 'loading' });
      const controller = new AbortController();
      source.loadContext(controller.signal).then((result) => {
        if (result.status === 'ok') setContextState({ status: 'ok', context: result.context });
        else setContextState({ status: result.status, message: result.message });
      });
      return;
    }
    fetchArea(applied, area, tableQuery);
  }, [applied, area, contextState.status, fetchArea, source, tableQuery]);

  /** Paginação/ordenação da tabela principal, com a mesma guarda de respostas antigas. */
  const changeTable = useCallback(
    (next: TableQuery) => {
      setTableQuery(next);
      const id = ++tableSeq.current;
      const areaSeq = requestSeq.current;
      setTableLoading(true);
      source
        .fetchTable(area, applied, next, new AbortController().signal)
        .then((table) => {
          if (id !== tableSeq.current || areaSeq !== requestSeq.current) return;
          setQuery((current) =>
            current.status === 'done' && current.response.body
              ? { status: 'done', response: { ...current.response, body: { ...current.response.body, table } } }
              : current
          );
        })
        .catch(() => undefined)
        .finally(() => {
          if (id === tableSeq.current) setTableLoading(false);
        });
    },
    [applied, area, source]
  );

  return {
    source,
    scenario,
    setScenario,
    contextState,
    context,
    area,
    today,
    applied,
    draft,
    draftChanged: JSON.stringify(draft) !== JSON.stringify(applied),
    notices,
    dismissNotices: () => setNotices([]),
    rangeError,
    moneyBasis,
    setMoneyBasis,
    query,
    tableQuery,
    tableLoading,
    selectArea,
    updateDraft,
    applyDraft,
    discardDraft,
    removeChip,
    clearFilters,
    retry,
    changeTable,
  };
}

export type ReportsController = ReturnType<typeof useReportsController>;
